import { useMediaQuery } from '@mui/material';
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  DateView,
  FieldSection,
  PickersLayoutContentWrapper,
  PickersLayoutProps,
  PickersLayoutRoot,
  pickersLayoutClasses,
  usePickerLayout,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { UseDateFieldProps } from '@mui/x-date-pickers/DateField';
import { DatePickerProps, DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { sub } from 'date-fns';
import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { useThemeContext } from '../../../global-state/themeContext';
import { PickerType } from '../../../global-state/types';
import { useRangeSelection } from '../../../hooks/useRangeSelection';
import i18n from '../../../i18n';
import { muiTheme } from '../../../theme';
import { localeMap } from '../../../utils/language';
import { Icon, Icons } from '../../atoms';
import { Button } from '../button/Button';
import { StyledButtonWrapper } from './DatePicker.styles';
import { createRangeDayComponent, createWeekDayComponent } from './DatePickerDayComponents';
import { createHoverHandlers, formatDateLabel, getRangeColors, isRangeMode } from './utils';

interface ButtonFieldProps
  extends UseDateFieldProps<Date, false>,
    BaseSingleInputFieldProps<Date | null, Date, FieldSection, false, DateValidationError> {
  label: string;
  isDropdown?: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showCalendarIcon?: boolean;
  disableDatePicker?: boolean;
  open?: boolean;
}

interface ButtonDatePickerProps
  extends Omit<DatePickerProps<Date>, 'open' | 'onOpen' | 'onClose' | 'onChange' | 'label' | 'onAccept'> {
  isDropdown?: boolean;
  label: string;
  onLabelChange: (label: string) => void;
  pickerType: PickerType;
  onChange: (date: Date | null, label: string) => void;
  showCalendarIcon?: boolean;
  disableDatePicker?: boolean;
  actionBar?: JSX.Element;
  onRangeChange?: (dates: [Date, Date], label: string) => void;
  onAccept?: (dates: [Date, Date]) => void;
}

const ButtonField: FC<ButtonFieldProps> = memo(
  ({
    id,
    label,
    isDropdown = false,
    setOpen,
    InputProps = {},
    inputProps = {},
    showCalendarIcon = false,
    open = false,
    disableDatePicker = false,
  }) => {
    const { ref } = InputProps;
    const ariaLabel = inputProps['aria-label'];

    return (
      <StyledButtonWrapper data-testid="date-picker-button" isDropdown={isDropdown}>
        {showCalendarIcon && <Icon icon={Icons.CALENDAR} size="small" />}
        <Button
          data-testid="date-picker-button-trigger"
          label={label}
          onClick={() => setOpen?.((prev) => !prev)}
          buttonContentProps={{
            labelPosition: 'left',
            iconOptions: {
              icon: Icons.DROPDOWN,
              size: 'small',
              ...(open ? { rotate: 'up' } : {}),
            },
          }}
          disabled={disableDatePicker}
          aria-disabled={disableDatePicker}
          variant="text"
          ref={ref}
          aria-label={ariaLabel}
          id={id}
        />
      </StyledButtonWrapper>
    );
  },
);

const CustomLayout: FC<PickersLayoutProps<Date | null, Date, DateView>> = memo((props) => {
  const { content, actionBar } = usePickerLayout(props);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const { mode } = useThemeContext();

  const commonColor = mode === 'dark' ? muiTheme.color.borderGray : muiTheme.color.textGray;
  const disabledColor = mode === 'dark' ? muiTheme.color.borderGrayDark : muiTheme.color.inActiveGray;

  return (
    <PickersLayoutRoot
      ownerState={props}
      sx={{
        '.MuiDayCalendar-weekNumberLabel, .MuiDayCalendar-weekDayLabel, .MuiDayCalendar-weekNumber': {
          color: commonColor,
        },
        '.MuiDayCalendar-weekContainer .Mui-disabled, .MuiPickersDay-root .Mui-disabled': {
          color: disabledColor,
        },
        ...(isMobile && {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }),
      }}
    >
      {actionBar}
      {content}
      <PickersLayoutContentWrapper className={pickersLayoutClasses.contentWrapper} />
    </PickersLayoutRoot>
  );
});

const ButtonDatePicker: FC<ButtonDatePickerProps> = (props) => {
  const {
    isDropdown,
    label,
    onLabelChange,
    pickerType,
    onChange,
    showCalendarIcon,
    disableDatePicker,
    actionBar,
    onRangeChange,
    onAccept,
    ...datePickerProps
  } = props;

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<Date | null>(null);
  const [selected, setSelected] = useState<Date | null>(null);
  const { mode } = useThemeContext();

  const rangeColors = getRangeColors(mode);
  const rangeState = useRangeSelection(pickerType, onRangeChange, onAccept);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (!date) return;

      if (isRangeMode(pickerType)) {
        const { shouldClose, label: rangeLabel, isValid } = rangeState.handleRangeSelection(date);
        if (rangeLabel) onLabelChange(rangeLabel);
        if (shouldClose && isValid) setOpen(false);
        return;
      }

      const newLabel = formatDateLabel(date, pickerType);
      setSelected(date);
      onLabelChange(newLabel);
      onChange(date, newLabel);
      setOpen(false);
    },
    [pickerType, rangeState.handleRangeSelection, onLabelChange, onChange],
  );

  const getDayComponent = useCallback(() => {
    switch (pickerType) {
      case 'week':
        return createWeekDayComponent(selected, hovered, setHovered);
      case 'range':
        return createRangeDayComponent(rangeColors, rangeState, hovered, setHovered);
      default:
        return undefined;
    }
  }, [pickerType, selected, hovered, rangeColors, rangeState]);

  const getDaySlotProps = useCallback(() => {
    if (pickerType === 'week') {
      return (ownerState: { day: Date }) => ({
        isHovered: hovered,
        isSelected: selected,
        ...createHoverHandlers(setHovered, ownerState.day),
      });
    }

    if (isRangeMode(pickerType)) {
      return (ownerState: { day: Date }) => ({
        isHovered: hovered,
        isSelected: rangeState.isDateRangeStart(ownerState.day) || rangeState.isDateRangeEnd(ownerState.day),
        disabled: rangeState.isDateDisabled(ownerState.day),
        ...createHoverHandlers(setHovered, ownerState.day),
      });
    }

    return undefined;
  }, [pickerType, selected, hovered, rangeState]);

  return (
    <MuiDatePicker
      slots={{
        field: ButtonField,
        layout: CustomLayout,
        actionBar: () => actionBar,
        day: getDayComponent(),
        ...datePickerProps.slots,
      }}
      slotProps={{
        ...datePickerProps.slotProps,
        field: { setOpen, isDropdown, label, showCalendarIcon, disableDatePicker, open } as never,
        day: getDaySlotProps(),
      }}
      {...datePickerProps}
      open={open}
      openTo={pickerType === 'month' ? 'month' : 'day'}
      onClose={() => {
        setOpen(false);
        rangeState.resetRange();
      }}
      onOpen={() => setOpen(true)}
      onChange={handleDateChange}
      closeOnSelect={!isRangeMode(pickerType)}
      onMonthChange={(date) => {
        if (pickerType === 'month') {
          handleDateChange(date);
        }
      }}
    />
  );
};

export type PickerProps = {
  inputSelectedDate: Date;
  label: string;
  disableFutureDates?: boolean;
  displayWeekNumber?: boolean;
  minDate?: Date;
  maxDate?: Date;
  views?: Array<'day' | 'month' | 'year'>;
  isDropdown?: boolean;
  onChange?: (dates: Date, label: string) => void;
  onRangeChange?: (dates: [Date, Date], label: string) => void;
  onAccept?: (dates: [Date, Date]) => void;
  pickerType?: PickerType;
  showCalendarIcon?: boolean;
  disableDatePicker?: boolean;
  actionBar?: JSX.Element;
};

export const DatePicker: FC<React.PropsWithChildren<PickerProps>> = ({
  inputSelectedDate,
  disableFutureDates = true,
  displayWeekNumber = true,
  isDropdown = false,
  views = ['day'],
  minDate = sub(new Date(), { months: 18 }),
  maxDate,
  onChange,
  onRangeChange,
  onAccept,
  pickerType = 'date',
  showCalendarIcon = false,
  disableDatePicker = false,
  actionBar,
  label,
}) => {
  const currentLocale = localeMap[i18n.language];
  const [selectedLabel, setSelectedLabel] = useState(label);

  const handleDateChange = useCallback(
    (date: Date | null, labelT: string) => {
      if (!date) return;
      setSelectedLabel(labelT);
      onChange?.(date, labelT);
    },
    [onChange],
  );

  useEffect(() => {
    setSelectedLabel(label);
  }, [label]);

  const handleRangeChange = useCallback(
    (dates: [Date, Date], labelT: string) => {
      setSelectedLabel(labelT);
      onRangeChange?.(dates, labelT);
    },
    [onRangeChange],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={currentLocale}>
      <ButtonDatePicker
        value={inputSelectedDate}
        onChange={handleDateChange}
        onRangeChange={handleRangeChange}
        onAccept={onAccept}
        disableFuture={disableFutureDates}
        displayWeekNumber={displayWeekNumber}
        views={views}
        minDate={minDate}
        maxDate={maxDate}
        label={selectedLabel}
        isDropdown={isDropdown}
        onLabelChange={setSelectedLabel}
        pickerType={pickerType}
        disableDatePicker={disableDatePicker}
        showCalendarIcon={showCalendarIcon}
        actionBar={actionBar}
      />
    </LocalizationProvider>
  );
};
